import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Play, Pause, Repeat, Bookmark, BookmarkCheck, ChevronLeft, ChevronRight, Loader2, User, Search } from "lucide-react";
import { SURAHS } from "../data/surahs";
import { RECITERS, TEACHERS, LISTENING_ONLY, audioUrl, isFullSurahOnly } from "../data/reciters";
import { Tx } from "../context/AutoTranslateContext";
import DOMPurify from "dompurify";
import "../styles/tajweed.css";

const AQC_TAJWEED = (n) => `https://api.alquran.cloud/v1/surah/${n}/quran-tajweed`;
const AQC_EN = (n) => `https://api.alquran.cloud/v1/surah/${n}/en.sahih`;
const AQC_MUYASSAR = (n) => `https://api.alquran.cloud/v1/surah/${n}/ar.muyassar`;

const BM_KEY = "icc_bookmarks";
const PREF_KEY = "icc_reader_prefs";

const loadBookmarks = () => {
  try { return JSON.parse(localStorage.getItem(BM_KEY) || "[]"); } catch { return []; }
};
const saveBookmarks = (bms) => {
  try { localStorage.setItem(BM_KEY, JSON.stringify(bms)); } catch (e) { console.debug("bookmark save failed:", e); }
};
const loadPrefs = () => {
  try { return JSON.parse(localStorage.getItem(PREF_KEY) || "{}"); } catch { return {}; }
};
const savePrefs = (p) => {
  try { localStorage.setItem(PREF_KEY, JSON.stringify(p)); } catch (e) { console.debug("prefs save failed:", e); }
};

const QuranReader = () => {
  const { surahNum } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const num = Math.min(114, Math.max(1, Number(surahNum) || 1));
  const surah = SURAHS[num - 1];

  const initialPrefs = loadPrefs();
  // Priority: ?reciter= URL param (from Teachers page) > saved pref > default Al-Husary
  const requestedReciter = searchParams.get("reciter");
  const validIds = new Set(RECITERS.map((r) => r.id));
  const [reciter, setReciter] = useState(
    (requestedReciter && validIds.has(requestedReciter))
      ? requestedReciter
      : (initialPrefs.reciter || TEACHERS[0].id)
  );
  const [showTranslation, setShowTranslation] = useState(initialPrefs.showTranslation ?? true);
  const [showTafsir, setShowTafsir] = useState(initialPrefs.showTafsir ?? true);
  const [repeatCount, setRepeatCount] = useState(initialPrefs.repeatCount || 1);
  const [autoPlayNext, setAutoPlayNext] = useState(initialPrefs.autoPlayNext ?? true);

  const [ayahsAr, setAyahsAr] = useState([]);   // tajweed markup HTML strings
  const [ayahsEn, setAyahsEn] = useState([]);
  const [ayahsTafsir, setAyahsTafsir] = useState([]); // Tafsir Al-Muyassar (Arabic)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [playing, setPlaying] = useState(null); // ayah number currently playing
  const [remaining, setRemaining] = useState(0); // repeats remaining for the current ayah
  const audioRef = useRef(null);

  const [bookmarks, setBookmarks] = useState(loadBookmarks);
  const [surahQuery, setSurahQuery] = useState("");

  useEffect(() => { savePrefs({ reciter, showTranslation, showTafsir, repeatCount, autoPlayNext }); },
    [reciter, showTranslation, showTafsir, repeatCount, autoPlayNext]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(""); setAyahsAr([]); setAyahsEn([]); setAyahsTafsir([]); setPlaying(null);
    Promise.all([fetch(AQC_TAJWEED(num)).then(r => r.json()),
                 fetch(AQC_EN(num)).then(r => r.json()),
                 fetch(AQC_MUYASSAR(num)).then(r => r.json())])
      .then(([taj, en, tafsir]) => {
        if (cancelled) return;
        setAyahsAr(taj?.data?.ayahs || []);
        setAyahsEn(en?.data?.ayahs || []);
        setAyahsTafsir(tafsir?.data?.ayahs || []);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError("Could not load surah. Please check your connection.");
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [num]);

  // ---- Audio playback with repeat & auto-advance ----
  const playAyah = (ayahNumberInSurah, repeats = repeatCount) => {
    setPlaying(ayahNumberInSurah);
    setRemaining(repeats);
    // scroll into view
    setTimeout(() => {
      const el = document.getElementById(`ayah-${ayahNumberInSurah}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };
  const stop = () => {
    setPlaying(null); setRemaining(0);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
  };

  useEffect(() => {
    if (!playing || !audioRef.current) return;
    audioRef.current.src = audioUrl(reciter, num, playing);
    audioRef.current.play().catch(() => {/* autoplay blocked */});
  }, [playing, reciter, num]);

  const onAudioEnded = () => {
    if (fullSurahReciter) {
      // Full-surah recording: the whole surah has finished. Stop cleanly.
      stop();
      return;
    }
    if (remaining > 1) {
      setRemaining((r) => r - 1);
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } else if (autoPlayNext && playing < ayahsAr.length) {
      playAyah(playing + 1, repeatCount);
    } else {
      stop();
    }
  };

  const toggleBookmark = (ayahN) => {
    const key = `${num}:${ayahN}`;
    const already = bookmarks.find((b) => b.key === key);
    const next = already
      ? bookmarks.filter((b) => b.key !== key)
      : [...bookmarks, { key, surah: num, ayah: ayahN, at: Date.now() }];
    setBookmarks(next); saveBookmarks(next);
  };
  const isBookmarked = (ayahN) => !!bookmarks.find((b) => b.key === `${num}:${ayahN}`);

  const filteredSurahs = useMemo(() => {
    const q = surahQuery.trim().toLowerCase();
    if (!q) return SURAHS;
    return SURAHS.filter((s) =>
      s.name.toLowerCase().includes(q) || String(s.n).includes(q) || s.arabic.includes(q)
    );
  }, [surahQuery]);

  const fullSurahReciter = isFullSurahOnly(reciter);

  return (
    <div data-testid="quran-reader" className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
      <audio ref={audioRef} onEnded={onAudioEnded} onError={onAudioEnded} preload="none" />

      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-xs text-mute mb-6">
        <Link to="/academy" className="hover:text-forest"><Tx>Academy</Tx></Link>
        <span>/</span>
        <span className="text-forest"><Tx>Qur'an Reader</Tx></span>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        {/* Sidebar — surah picker + reader settings */}
        <aside className="space-y-4">
          <div className="clay-surface rounded-3xl p-4">
            <div className="relative mb-3">
              <Search className="w-3.5 h-3.5 text-mute absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="surah-search"
                type="text" placeholder="Find surah…" value={surahQuery}
                onChange={(e) => setSurahQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-xs rounded-full bg-cream border border-forest/10 outline-none focus:border-forest transition"
              />
            </div>
            <div className="max-h-[420px] overflow-y-auto space-y-1 -mx-1 px-1">
              {filteredSurahs.map((s) => (
                <button
                  key={s.n}
                  data-testid={`surah-btn-${s.n}`}
                  onClick={() => nav(`/academy/quran/${s.n}`)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-2xl text-left transition ${
                    s.n === num ? "bg-forest text-sand" : "hover:bg-cream text-ink"
                  }`}
                >
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold ${
                    s.n === num ? "bg-sand/20 text-sand" : "bg-forest/10 text-forest"
                  }`}>{s.n}</span>
                  <span className="flex-1 truncate">
                    <span className="block text-sm font-medium">{s.name}</span>
                    <span className="block text-[10px] opacity-70">{s.ayahs} <Tx>ayat</Tx> · {s.revelation}</span>
                  </span>
                  <span className={`arabic text-base ${s.n === num ? "text-gold" : "text-forest/60"}`}>{s.arabic}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Reader controls */}
          <div className="clay-surface rounded-3xl p-4 space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-gold flex items-center gap-1.5 mb-1.5">
                <User className="w-3 h-3" /> <Tx>Reciter</Tx>
              </label>
              <select
                data-testid="reciter-select"
                value={reciter} onChange={(e) => setReciter(e.target.value)}
                className="w-full bg-cream border border-forest/10 rounded-2xl px-3 py-2 text-sm outline-none focus:border-forest"
              >
                <optgroup label="⭐ Tajweed Teachers (for learning)">
                  {TEACHERS.map((r) => (
                    <option key={r.id} value={r.id}>{`${r.name} — ${r.style}`}</option>
                  ))}
                </optgroup>
                <optgroup label="Full listening library">
                  {LISTENING_ONLY.map((r) => (
                    <option key={r.id} value={r.id}>{`${r.name} (${r.country})`}</option>
                  ))}
                </optgroup>
              </select>
              {fullSurahReciter && (
                <p data-testid="full-surah-notice" className="mt-2 text-[11px] text-gold italic leading-relaxed">
                  <Tx>This reciter recorded the whole surah in one file — per-ayah Play buttons are hidden. Use "Play full surah" at the top to listen from the start.</Tx>
                </p>
              )}
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-gold flex items-center gap-1.5 mb-1.5">
                <Repeat className="w-3 h-3" /> <Tx>Repeat each ayah</Tx>
              </label>
              <div className="flex gap-1">
                {[1, 3, 5, 10].map((n) => (
                  <button
                    key={n}
                    data-testid={`repeat-${n}`}
                    onClick={() => setRepeatCount(n)}
                    className={`flex-1 py-1.5 text-xs rounded-full transition ${
                      repeatCount === n ? "bg-forest text-sand" : "bg-cream text-mute hover:text-forest"
                    }`}
                  >{n}×</button>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-xs text-ink cursor-pointer">
              <input
                data-testid="toggle-translation"
                type="checkbox" checked={showTranslation}
                onChange={(e) => setShowTranslation(e.target.checked)}
                className="w-4 h-4 rounded border-forest/20 accent-forest"
              />
              <Tx>Show English translation</Tx>
            </label>
            <label className="flex items-center gap-2 text-xs text-ink cursor-pointer">
              <input
                data-testid="toggle-tafsir"
                type="checkbox" checked={showTafsir}
                onChange={(e) => setShowTafsir(e.target.checked)}
                className="w-4 h-4 rounded border-forest/20 accent-forest"
              />
              <Tx>Show Tafsir Al-Muyassar (Arabic)</Tx>
            </label>
            <label className="flex items-center gap-2 text-xs text-ink cursor-pointer">
              <input
                data-testid="toggle-autoplay"
                type="checkbox" checked={autoPlayNext}
                onChange={(e) => setAutoPlayNext(e.target.checked)}
                className="w-4 h-4 rounded border-forest/20 accent-forest"
              />
              <Tx>Auto-play next ayah</Tx>
            </label>
          </div>

          {bookmarks.length > 0 && (
            <div className="clay-surface rounded-3xl p-4">
              <p className="text-[10px] uppercase tracking-widest text-gold mb-2 flex items-center gap-1.5">
                <BookmarkCheck className="w-3 h-3" /> <Tx>Your bookmarks</Tx>
              </p>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {bookmarks.slice().reverse().slice(0, 20).map((b) => (
                  <button
                    key={b.key}
                    data-testid={`bookmark-${b.key}`}
                    onClick={() => nav(`/academy/quran/${b.surah}`)}
                    className="w-full text-left px-3 py-1.5 rounded-xl hover:bg-cream text-xs text-ink"
                  >
                    <span className="text-forest font-medium">{SURAHS[b.surah - 1].name}</span>
                    <span className="text-mute"> · <Tx>Ayah</Tx> {b.ayah}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </aside>

        {/* Main reader */}
        <main>
          <div className="mb-8 text-center">
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-2">{surah.revelation} · {surah.ayahs} <Tx>ayat</Tx></p>
            <p className="arabic text-gold text-3xl mb-1">{surah.arabic}</p>
            <h1 className="font-heading text-4xl sm:text-5xl text-forest font-bold">{surah.name}</h1>

            <div className="flex items-center justify-center gap-2 mt-5">
              <button
                data-testid="prev-surah"
                onClick={() => nav(`/academy/quran/${Math.max(1, num - 1)}`)}
                disabled={num === 1}
                className="p-2 rounded-full bg-cream border border-forest/10 hover:border-forest transition disabled:opacity-40"
                aria-label="Previous surah"
              >
                <ChevronLeft className="w-4 h-4 text-forest" />
              </button>
              <button
                data-testid="play-surah"
                onClick={() => (playing ? stop() : playAyah(1))}
                className="px-5 py-2 bg-forest text-sand hover:bg-sage rounded-full text-sm font-medium flex items-center gap-2 transition"
              >
                {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {playing ? <Tx>Stop</Tx> : <Tx>Play full surah</Tx>}
              </button>
              <button
                data-testid="next-surah"
                onClick={() => nav(`/academy/quran/${Math.min(114, num + 1)}`)}
                disabled={num === 114}
                className="p-2 rounded-full bg-cream border border-forest/10 hover:border-forest transition disabled:opacity-40"
                aria-label="Next surah"
              >
                <ChevronRight className="w-4 h-4 text-forest" />
              </button>
            </div>
          </div>

          {num !== 9 && num !== 1 && (
            <p data-testid="bismillah" className="arabic text-center text-2xl text-forest mb-8">
              بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ
            </p>
          )}

          {loading && (
            <div className="text-center py-16 text-mute">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              <p className="text-sm"><Tx>Loading surah…</Tx></p>
            </div>
          )}
          {error && (
            <div className="text-center py-12 text-clay bg-clay/10 rounded-2xl">{error}</div>
          )}

          <div className="space-y-5">
            {ayahsAr.map((a, i) => {
              const enText = ayahsEn[i]?.text || "";
              const ayahN = a.numberInSurah;
              const isPlaying = playing === ayahN;
              const bookmarked = isBookmarked(ayahN);
              // AlQuran API returns tajweed as bracket markup: `[class[text]`
              // where class is one letter (h,s,l,n,p,m,o,q,c,f,w,i,a,u,d,b) sometimes with :id.
              const arabicHtml = a.text.replace(
                /\[([a-z])(?::\d+)?\[([^\]]+)\]/g,
                (_, cls, txt) => `<span class="tj-${cls}">${txt}</span>`
              );
              return (
                <article
                  key={ayahN}
                  id={`ayah-${ayahN}`}
                  data-testid={`ayah-${ayahN}`}
                  className={`clay-surface rounded-3xl p-6 sm:p-8 ${isPlaying ? "ring-2 ring-gold/60" : ""}`}
                >
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    {!fullSurahReciter && (
                      <button
                        data-testid={`play-ayah-${ayahN}`}
                        onClick={() => (isPlaying ? stop() : playAyah(ayahN))}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition ${
                          isPlaying ? "bg-gold text-forest" : "bg-forest text-sand hover:bg-sage"
                        }`}
                      >
                        {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                        {isPlaying ? <Tx>Stop</Tx> : <Tx>Play</Tx>}
                      </button>
                    )}
                    <button
                      data-testid={`bookmark-ayah-${ayahN}`}
                      onClick={() => toggleBookmark(ayahN)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition ${
                        bookmarked ? "bg-gold/20 text-gold" : "bg-cream text-mute hover:text-forest"
                      }`}
                    >
                      {bookmarked ? <BookmarkCheck className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
                      {bookmarked ? <Tx>Saved</Tx> : <Tx>Bookmark</Tx>}
                    </button>
                    {isPlaying && remaining > 1 && (
                      <span data-testid="repeat-counter" className="text-[10px] uppercase tracking-widest text-gold">
                        <Tx>Repeats left</Tx>: {remaining}
                      </span>
                    )}
                    <span className="ml-auto ayah-num">{ayahN}</span>
                  </div>

                  <div
                    className={`tajweed-verse ${isPlaying ? "is-playing" : ""}`}
                    // AlQuran API returns markup as text (with actual < > tokens) — render as HTML
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(arabicHtml) }}
                  />

                  {showTranslation && (
                    <p data-testid={`en-${ayahN}`} className="text-sm text-ink/85 leading-relaxed mt-4 italic border-l-2 border-gold/40 pl-4">
                      {enText}
                      <span className="not-italic text-[10px] text-mute ml-2">— Sahih International</span>
                    </p>
                  )}
                  {showTafsir && ayahsTafsir[i]?.text && (
                    <div data-testid={`tafsir-${ayahN}`} className="mt-4 bg-cream/70 border border-gold/20 rounded-2xl p-4">
                      <p className="text-[10px] uppercase tracking-widest text-gold mb-2"><Tx>Tafsir Al-Muyassar</Tx> · التفسير الميسر</p>
                      <p className="arabic text-base text-ink leading-loose text-right" dir="rtl">{ayahsTafsir[i].text}</p>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </main>
      </div>
    </div>
  );
};

export default QuranReader;
